// taskService.js
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { TASK_STATUS } from "../utils";

// Task Priority Constants
export const TASK_PRIORITY = {
  LOW: "low",
  MEDIUM: "medium", 
  HIGH: "high",
  CRITICAL: "critical"
};

const tasksCollection = collection(db, "tasks");
const tasksAssignedCollection = collection(db, "tasksAssigned");

export const getTasks = async () => {
  const snapshot = await getDocs(tasksCollection);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const createTask = async (taskData) => {
  const payload = {
    ...taskData,
    status: TASK_STATUS.not_started,
    priority: taskData.priority || TASK_PRIORITY.MEDIUM,
    createdAt: Date.now(),
    projectId: taskData.projectId || null, // Link task to project
    projectName: taskData.projectName || null, // Store project name for easier display
  };
  const docRef = await addDoc(tasksCollection, payload);

  // If task is linked to a project, update project's task list
  if (taskData.projectId) {
    try {
      const { addTaskToProject } = await import("./projectService");
      await addTaskToProject(taskData.projectId, docRef.id);
    } catch (error) {
      console.error("Error linking task to project:", error);
    }
  }

  return docRef.id;
};

export const updateTask = async (taskId, updatedData) => {
  const taskDoc = doc(db, "tasks", taskId);
  await updateDoc(taskDoc, updatedData);
};

export const deleteTask = async (taskId) => {
  const taskDoc = doc(db, "tasks", taskId);
  await deleteDoc(taskDoc);
};

export const assignTaskToStudents = async (assignTask) => {
  const payload = {
    ...assignTask,
    status: TASK_STATUS.not_started,
    priority: assignTask.priority || TASK_PRIORITY.MEDIUM,
    createdAt: Date.now(),
    projectId: assignTask.projectId || null, // Link assigned task to project
    projectName: assignTask.projectName || null, // Store project name for easier display
  };
  const docRef = await addDoc(tasksAssignedCollection, payload);
  return { id: docRef.id, ...payload };
};

// get all tasks assigned
export const getAllAssignedTasks = async () => {
  const snapshot = await getDocs(tasksAssignedCollection);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// get tasks assigned to a specific user
export const getAssignedTasksByUserId = async (userId) => {
  const q = query(tasksAssignedCollection, where("userId", "==", userId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// get tasks by project ID
export const getTasksByProjectId = async (projectId) => {
  const q = query(tasksCollection, where("projectId", "==", projectId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// get assigned tasks by project ID
export const getAssignedTasksByProjectId = async (projectId) => {
  const q = query(tasksAssignedCollection, where("projectId", "==", projectId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// update task status and recalculate project progress
export const updateTaskStatus = async (
  taskId,
  status,
  isAssignedTask = false
) => {
  const collection_name = isAssignedTask ? "tasksAssigned" : "tasks";
  const taskDoc = doc(db, collection_name, taskId);

  await updateDoc(taskDoc, {
    status,
    updatedAt: Date.now(),
  });

  // Get the task to find its project
  const taskSnapshot = await getDocs(
    query(collection(db, collection_name), where("__name__", "==", taskId))
  );

  if (!taskSnapshot.empty) {
    const taskData = taskSnapshot.docs[0].data();
    if (taskData.projectId) {
      // Update project progress
      try {
        const { updateProjectProgress } = await import("./projectService");
        await updateProjectProgress(taskData.projectId);
      } catch (error) {
        console.error("Error updating project progress:", error);
      }
    }
  }
};
